import { ChangeDetectionStrategy, Component, type OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { faCircleNodes, faReceipt, type IconDefinition } from '@fortawesome/free-solid-svg-icons';
import { MerchantChangeService, MerchantService } from '@odin/odin-core';
import { RoleGuardService } from '@odin/odin-authentication';

export interface SettingsCard {
  title: string;
  description: string;
  route: string;
  icon: IconDefinition;
  testId: string;
}

@Component({
  standalone: false,
  selector: 'odin-settings-home-page',
  templateUrl: './settings-home-page.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './settings-home-page.component.scss',
})
export class SettingsHomePageComponent implements OnDestroy {
  public buidName = '';
  private readonly merchantChangeSub: Subscription;

  public readonly cards: SettingsCard[] = [
    {
      title: 'Receipts',
      description: 'Configure merchant and customer receipts, including email subject, recipients and SMS.',
      route: '/settings/receipts',
      icon: faReceipt,
      testId: 'settings-card-receipts',
    },
    {
      title: 'Integrations',
      description: 'Manage Checkout access tokens, Apple Pay domains, Xero, WooCommerce and webhooks.',
      route: '/settings/integrations',
      icon: faCircleNodes,
      testId: 'settings-card-integrations',
    },
  ];

  constructor(
    private router: Router,
    private merchantService: MerchantService,
    private merchantChangeService: MerchantChangeService,
    private roleGuardService: RoleGuardService,
  ) {
    if (!this.roleGuardService.hasPermission('settings:View')) {
      this.router.navigate(['/dashboard']);
    }

    this.refreshBuidName();
    this.merchantChangeSub = this.merchantChangeService.merchantChangeEvent.subscribe(() => this.refreshBuidName());
  }

  ngOnDestroy(): void {
    this.merchantChangeSub?.unsubscribe();
  }

  public get pageTitle(): string {
    return this.buidName ? `${this.buidName} Settings` : 'Settings';
  }

  public open(card: SettingsCard): void {
    this.router.navigate([card.route]);
  }

  private refreshBuidName(): void {
    this.buidName = this.merchantService.getCurrentBuid()?.name ?? '';
  }
}
